import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";

/**
 * NotFound.jsx — Cosmic Lost-in-Space 404 Page
 * A React.js component that replicates the attached video.
 *
 * Usage:
 *   <Route path="*" element={<NotFound />} />
 */

export default function NotFound() {
  const panelRef = useRef(null);
  const rainRef = useRef(null);
  const [soundOn, setSoundOn] = useState(false);
  const audioCtxRef = useRef(null);
  const ambientNodesRef = useRef([]);

  useEffect(() => {
    if (!soundOn) {
      ambientNodesRef.current.forEach(n => {
        try { n.stop?.(); n.disconnect?.(); } catch {}
      });
      ambientNodesRef.current = [];
      if (audioCtxRef.current) {
        try { audioCtxRef.current.close(); } catch {}
        audioCtxRef.current = null;
      }
      return;
    }

    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    audioCtxRef.current = ctx;

    const createOsc = (type, freq, gainVal) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = type;
      osc.frequency.value = freq;
      gain.gain.value = gainVal;
      osc.connect(gain).connect(ctx.destination);
      osc.start();
      ambientNodesRef.current.push(osc, gain);
      return { osc, gain };
    };

    const { osc, gain } = createOsc("sawtooth", 52, 0.015);
    const lfo = ctx.createOscillator();
    const lfoGain = ctx.createGain();
    lfo.frequency.value = 0.08;
    lfoGain.gain.value = 8;
    lfo.connect(lfoGain).connect(osc.frequency);
    lfo.start();
    ambientNodesRef.current.push(lfo, lfoGain);

    const high = ctx.createOscillator();
    const highGain = ctx.createGain();
    high.type = "triangle";
    high.frequency.value = 440;
    high.detune.value = 1200;
    highGain.gain.value = 0.006;
    high.connect(highGain).connect(ctx.destination);
    high.start();
    ambientNodesRef.current.push(high, highGain);

    return () => {
      ambientNodesRef.current.forEach(n => {
        try { n.stop?.(); n.disconnect?.(); } catch {}
      });
      ambientNodesRef.current = [];
      try { ctx.close(); } catch {}
      audioCtxRef.current = null;
    };
  }, [soundOn]);

  useEffect(() => {
    const canvas = rainRef.current;
    if (!canvas) return;
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    const ctx = canvas.getContext("2d");

    function size() {
      const box = panelRef.current?.getBoundingClientRect();
      const w = Math.max(320, Math.floor((box?.width || window.innerWidth) - 64));
      const h = Math.max(240, Math.floor((box?.height || window.innerHeight) - 64));
      canvas.width = w * dpr;
      canvas.height = h * dpr;
      canvas.style.width = w + "px";
      canvas.style.height = h + "px";
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    }

    size();
    window.addEventListener("resize", size);

    const stars = Array.from({ length: 160 }, () => ({
      x: Math.random(),
      y: Math.random(),
      s: Math.random() * 1 + 0.2,
      v: Math.random() * 0.6 + 0.2,
    }));

    let raf;
    function tick() {
      const w = canvas.width / dpr;
      const h = canvas.height / dpr;
      ctx.clearRect(0, 0, w, h);

      const g = ctx.createLinearGradient(w * 0.49, 0, w * 0.51, h);
      g.addColorStop(0, "rgba(150,180,255,0.10)");
      g.addColorStop(0.5, "rgba(160,220,255,0.35)");
      g.addColorStop(1, "rgba(180,120,255,0.10)");
      ctx.fillStyle = g;
      ctx.fillRect(w * 0.36, 0, w * 0.28, h);

      ctx.globalCompositeOperation = "lighter";
      ctx.strokeStyle = "rgba(200,220,255,0.5)";
      stars.forEach(st => {
        st.y += st.v * 0.8 / 60;
        if (st.y > 1) st.y = -Math.random() * 0.2;
        const x = st.x * w;
        const y = st.y * h;
        ctx.lineWidth = st.s;
        ctx.beginPath();
        ctx.moveTo(x, y - 14 * st.s);
        ctx.lineTo(x, y + 4 * st.s);
        ctx.stroke();
      });
      ctx.globalCompositeOperation = "source-over";

      raf = requestAnimationFrame(tick);
    }

    raf = requestAnimationFrame(tick);
    return () => { cancelAnimationFrame(raf); window.removeEventListener("resize", size); };
  }, []);

  const marquee = useMemo(() => {
    const msg = "WHOOPS! LOOKS LIKE YOU'RE LOST AMONG THE STARS  ";
    return new Array(12).fill(msg).join(" ");
  }, []);

  const reduceMotion = typeof window !== "undefined" &&
    window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches;

  return (
    <div className="x404-root" role="main" aria-labelledby="nf-title">
      <div className="x404-backdrop" aria-hidden="true" />
      <section ref={panelRef} className="x404-panel">
        <header className="x404-top">
          <div className="x404-logo" aria-label="Te.C.H-e" />
          <div className="x404-marquee" aria-hidden="true">
            <div className="x404-marquee-track">{marquee}</div>
          </div>
          <button
            type="button"
            className="x404-sound btn"
            aria-pressed={soundOn}
            onClick={() => setSoundOn(v => !v)}
            title="Toggle ambient sound"
          >
            <span>SOUND</span>
            <span className="sep">|</span>
            <span className={soundOn ? "on" : "off"}>{soundOn ? "ON" : "OFF"}</span>
          </button>
        </header>

        <div className="x404-canvasWrap">
          <canvas ref={rainRef} className="x404-rain" aria-hidden="true" />
          <div className="x404-numbers" aria-hidden>
            <svg viewBox="0 0 1200 360" className="x404-svg">
              <defs>
                <linearGradient id="numGlow" x1="0%" y1="0%" x2="0%" y2="100%">
                  <stop offset="0%" stopColor="rgba(180,200,200,0.35)" />
                  <stop offset="100%" stopColor="rgba(160,180,180,0.25)" />
                </linearGradient>
              </defs>
              <g fill="none" stroke="url(#numGlow)" strokeWidth="40" strokeLinecap="round" strokeLinejoin="round" opacity="0.8">
                <path d="M180 300 L180 120 L60 240 L300 240" />                 {/* left 4 */}
                <rect x="450" y="80" width="240" height="200" rx="60" ry="60" />{/* zero */}
                <path transform="translate(90 0)" d="M900 300 L900 120 L780 240 L1020 240" />               {/* right 4 */}
              </g>
            </svg>
          </div>

          <div className="x404-cta">
            <RocketHome />
          </div>
        </div>
      </section>

      <style>{`
        .x404-root { position:relative; min-height:100svh; background:#0b0d10; overflow:hidden; color:#d6dde5; }
        .x404-backdrop { position:absolute; inset:0; background: url('https://images.unsplash.com/photo-1485738422979-f5c462d49f74?q=80&w=1600&auto=format&fit=crop') center/cover no-repeat; filter: blur(4px) brightness(0.8); transform: scale(1.05); }
        .x404-panel { position:relative; margin:4vmin auto; max-width:1200px; border-radius:18px; background: #050607; box-shadow: 0 10px 40px rgba(0,0,0,0.6); outline:1px solid rgba(255,255,255,0.04); overflow:hidden; }
        @media (max-width: 720px){ .x404-panel{ margin: 0; border-radius: 0; } }
        .x404-top{ display:grid; grid-template-columns: 64px 1fr auto; align-items:center; gap:12px; padding:16px 18px; }
        .x404-logo{ width:28px; height:28px; border-radius:6px; background: conic-gradient(from 0deg, #ff9d00, #cc2, #6df, #9f6, #ff9d00); mask: radial-gradient(circle at 30% 30%, transparent 6px, black 7px), linear-gradient(black,black); mask-composite: exclude; -webkit-mask-composite: xor; filter: saturate(0.9) brightness(1.1); }
        .x404-marquee{ overflow:hidden; white-space:nowrap; opacity:0.8; font: 600 11px/1.8 "IBM Plex Mono", ui-monospace, SFMono-Regular, Menlo, monospace; letter-spacing:2px; color:#b4beca; }
        .x404-marquee-track{ display:inline-block; padding-left:100%; animation: scroll 30s linear infinite; }
        .x404-sound.btn{ font: 700 10px/1.2 "IBM Plex Mono", ui-monospace, monospace; letter-spacing:1.5px; color:#b7c2cf; background: rgba(255,255,255,0.04); border:1px solid rgba(255,255,255,0.06); padding:10px 12px; border-radius:10px; cursor:pointer; transition: .2s ease; backdrop-filter: blur(4px); }
        .x404-sound.btn:hover{ background: rgba(255,255,255,0.08); }
        .x404-sound .sep{ opacity:.4; margin: 0 8px; }
        .x404-sound .on{ color:#9cff9c; }
        .x404-sound .off{ color:#ffaeae; }
        .x404-canvasWrap{ position:relative; min-height: min(72svh, 800px); display:flex; align-items:center; justify-content:center; }
        .x404-rain{ position:absolute; inset:0; width:100%; height:100%; display:block; }
        .x404-numbers{ position:absolute; inset:0; display:grid; place-items:center; pointer-events:none; opacity:0.9; }
        .x404-svg{ width:min(92vw, 1100px); height:auto; filter: drop-shadow(0 18px 40px rgba(100,180,255,0.08)); }
        .x404-cta{ position:relative; z-index:2; margin: 28px 0 60px; }
        @keyframes scroll { from { transform: translateX(0); } to { transform: translateX(-50%); } }
        ${reduceMotion ? `.x404-marquee-track{ animation:none; }` : ""}
      `}</style>
    </div>
  );
}

function RocketHome(){
  const [hover, setHover] = useState(false);
  const goHome = () => {
    try { window.scrollTo({ top: 0, behavior: 'smooth' }); } catch {}
  };

  return (
    <Link to="/" onClick={goHome} className="rock-btn" aria-label="Go back home">
      <span className="lab">LET'S ROCKET BACK HOME</span>
      <span className={"rocket" + (hover ? " lift" : "")} onMouseEnter={() => setHover(true)} onMouseLeave={() => setHover(false)} aria-hidden>
        <svg viewBox="0 0 64 64" width="20" height="20">
          <path d="M40 8c-8 4-12 12-14 20l10 10c8-2 16-6 20-14 2-4-2-10-6-14s-10-8-14-6z" fill="#d9ffdc"/>
          <circle cx="44" cy="20" r="4" fill="#94f8ff"/>
          <path d="M26 36l-8 2 2-8z" fill="#ffa07a"/>
        </svg>
        <span className="trail" />
      </span>
      <style>{`
        .rock-btn{ position:relative; display:inline-flex; align-items:center; gap:10px; padding:14px 18px; border-radius:999px; background: linear-gradient(180deg,#1d2e1f,#101b11); color:#e8f6ea; border:1px solid rgba(110,255,160,0.25); box-shadow: inset 0 0 0 1px rgba(0,0,0,0.4), 0 8px 32px rgba(40,140,90,0.35); text-decoration:none; font: 700 11px/1.2 "IBM Plex Mono", ui-monospace, monospace; letter-spacing:1.5px; }
        .rock-btn:hover{ transform: translateY(-1px); box-shadow: inset 0 0 0 1px rgba(0,0,0,0.4), 0 12px 42px rgba(60,200,120,0.45); }
        .rock-btn:active{ transform: translateY(0); }
        .rock-btn .lab{ padding: 2px 2px 2px 8px; }
        .rocket{ position:relative; display:inline-flex; align-items:center; justify-content:center; }
        .rocket .trail{ position:absolute; left:-16px; width:16px; height:4px; border-radius:4px; background: radial-gradient(closest-side, rgba(250,255,200,0.9), rgba(255,200,120,0.55), transparent 70%); filter: blur(0.5px); animation: flick 0.3s infinite; }
        @keyframes flick{ 0%{ transform: scaleX(0.8); opacity:0.75;} 50%{ transform: scaleX(1.2); opacity:1;} 100%{ transform: scaleX(0.9); opacity:0.7;} }
        .lift{ transform: translateY(-1px); }
      `}</style>
    </Link>
  );
}